/**
 * Shared field shell for both browse bars (`ListBrowseToolbar`, `UnifiedFilterBar`) —
 * 32px resting height (grows when unified chips wrap), identical border, background,
 * and focus-within ring. Lead icon is always `Search`; clear affordance is a plain `X`.
 */
export const FILTER_FIELD_SHELL_CLASS =
  'min-h-8 min-w-[min(100%,12rem)] flex-1 basis-[14rem] rounded-md border border-border/50 bg-background/70 transition-colors focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40'

export const MINIMAL_FILTER_FIELD_CLASS =
  'h-8 rounded-md border-border/70 bg-background px-2.5 text-sm shadow-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/40'

/**
 * Always-on Visible Fields controls (select / text / date trigger).
 * Solid `bg-background` — do not mix with transparent Select/Input defaults.
 */
export const VISIBLE_FILTER_FIELD_CONTROL_CLASS = MINIMAL_FILTER_FIELD_CLASS

export const FILTER_CHIP_LABEL_CLASS = 'truncate text-muted-foreground'

export const FILTER_CHIP_VALUE_CLASS = 'truncate font-medium'

/** Strong ease-out for filter press / popover motion (Emil: avoid sluggish ease-in). */
export const FILTER_EASE_OUT = 'ease-[cubic-bezier(0.23,1,0.32,1)]'

/** Shared transform transition for press feedback (pair with shell/control press classes). */
export const FILTER_PRESS_TRANSITION_CLASS = `transition-transform duration-150 ${FILTER_EASE_OUT} motion-reduce:transition-none`

/**
 * Whole-chip press feedback — scale the shell when any segment is active.
 * Prefer this over per-segment scale (segments look broken when they scale alone).
 */
export const FILTER_CHIP_SHELL_PRESS_CLASS = [
  FILTER_PRESS_TRANSITION_CLASS,
  'has-[:active]:scale-[0.97] motion-reduce:has-[:active]:scale-100',
].join(' ')

/** Single-control press (value pills, clear / help icon buttons). */
export const FILTER_CONTROL_PRESS_CLASS = [
  FILTER_PRESS_TRANSITION_CLASS,
  'active:scale-[0.97] motion-reduce:active:scale-100',
].join(' ')

/** Shared filter popover enter/exit — fade + tiny slide, no scale; origin from trigger. */
export const FILTER_POPOVER_CONTENT_CLASS =
  'origin-[var(--radix-popover-content-transform-origin)] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 motion-reduce:animate-none'

/** Touch-safe hover tints — full class strings so Tailwind can detect them. */
export const FILTER_HOVER_BG_MUTED =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted'
export const FILTER_HOVER_BG_PRIMARY_20 =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/20'
export const FILTER_HOVER_BG_PRIMARY_25 =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/25'
export const FILTER_HOVER_BG_ACCENT_60 =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:bg-accent/60'
export const FILTER_HOVER_TEXT_FOREGROUND =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground'
export const FILTER_HOVER_TEXT_PRIMARY =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:text-primary'
export const FILTER_HOVER_BORDER_MUTED =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:border-muted-foreground/60'
export const FILTER_HOVER_BORDER_BORDER =
  '[@media(hover:hover)_and_(pointer:fine)]:hover:border-border'

/** Borderless empty-state add pill (`+ Label`) for primary pill-bar dimensions — full-opacity surface. */
export const FILTER_ADD_PILL_CLASS = [
  'inline-flex h-6.5 max-w-full items-center gap-1 rounded-[var(--radius-button)] border border-dashed border-muted-foreground/40 bg-background px-2.5 text-xs text-muted-foreground',
  'transition-[color,background-color,border-color,transform] duration-150',
  FILTER_EASE_OUT,
  FILTER_HOVER_BORDER_MUTED,
  FILTER_HOVER_BG_MUTED,
  FILTER_HOVER_TEXT_FOREGROUND,
  'active:scale-[0.97] motion-reduce:active:scale-100 motion-reduce:transition-none',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
].join(' ')

/** Left-segment label for committed free-text search in `UnifiedFilterBar` — the operator word is appended by the bar. */
export const UNIFIED_TEXT_SEARCH_CHIP_LABEL = 'Keywords'

export const UNIFIED_TEXT_CHIP_MAX_DISPLAY = 24

export function truncateChipDisplay(value: string, maxLength = UNIFIED_TEXT_CHIP_MAX_DISPLAY): string {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength)}…`
}

/** Shared row styling for filter option lists, operator lists, and picker rows. */
export const FILTER_OPTION_ROW_CLASS = [
  'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors duration-150',
  FILTER_EASE_OUT,
  '[@media(hover:hover)_and_(pointer:fine)]:hover:bg-accent [@media(hover:hover)_and_(pointer:fine)]:hover:text-accent-foreground',
  'focus-visible:bg-accent focus-visible:text-accent-foreground',
].join(' ')

/** Taller rows for unified typeahead suggestion navigation (not value pickers). */
export const FILTER_SUGGESTION_ROW_CLASS =
  'flex h-9 w-full cursor-default select-none items-center gap-2.5 rounded-sm px-2.5 text-left text-sm outline-none transition-colors'

/** Neutral checkbox in filter option lists — always visible for multi-select alignment. */
export const FILTER_OPTION_CHECKBOX_CLASS =
  'pointer-events-none size-4 shrink-0 shadow-none border-foreground data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background'
